// src/pages/SubOrgPortfolios.tsx
import { useEffect, useRef, useState } from "react";
import { getQuestionnaireDisplayTitle } from "@/lib/questionnaireDisplayTitle";
import { useNavigate, useParams } from "react-router-dom";

import AppLayout from "@/components/layout/AppLayout";
import { ClientAdminBreadcrumb } from "@/components/layout/ClientAdminBreadcrumb";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  SERVICE_MODEL_OPTIONS,
  questionnaireUsesServiceModels,
  serviceModelsRequired,
  validateServiceModels,
} from "@/lib/serviceModels";

import {
  Plus,
  Building2,
  Calendar,
  ChevronRight,
  Loader2,
  ArrowLeft,
  Users,
  ClipboardList,
  UserPlus,
} from "lucide-react";

// Category options
const CATEGORY_OPTIONS = [
  { value: "Cybersecurity Starter Assessments", label: "Cybersecurity Starter Assessments" },
  { value: "Telecom (Fixed/ISP)", label: "Telecommunications" },
  { value: "Risk Exposure", label: "Risk Exposure" },
  { value: "Pre-Investment Triage", label: "Pre-Investment Triage" },
  { value: "Media", label: "Media" },
  { value: "Private Equity", label: "Private Equity" },
  { value: "Data Center", label: "Data Center" },
  { value: "Gen AI", label: "Gen AI" },
];

// Phase 1 (Service Model Foundation) — shared source of truth.
const TELECOM_SERVICE_MODEL_OPTIONS = SERVICE_MODEL_OPTIONS;

interface CategoryQuestionnaire {
  questionnaire_key: string;
  is_default: boolean;
  questionnaires: {
    id: string;
    meta: { name?: string; description?: string } | null;
  } | null;
}

interface Portfolio {
  id: string;
  name: string;
  category: string | null;
  region: string | null;
  sector: string | null;
  created_at: string;
}

interface SubOrg {
  id: string;
  name: string;
  client_id: string;
}

interface Client {
  id: string;
  name: string;
}

interface PortfolioManager {
  user_id: string;
  role: string;
  created_at: string;
  display_name?: string | null;
}

export default function SubOrgPortfolios() {
  const { subOrgId } = useParams<{ subOrgId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [subOrg, setSubOrg] = useState<SubOrg | null>(null);
  const [client, setClient] = useState<Client | null>(null);
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [portfolioManagers, setPortfolioManagers] = useState<PortfolioManager[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Portfolio modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newCategory, setNewCategory] = useState("");
  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState("");
  const [availableAssessments, setAvailableAssessments] = useState<CategoryQuestionnaire[]>([]);
  const [serviceModels, setServiceModels] = useState<string[]>([]);
  const [serviceModelOther, setServiceModelOther] = useState("");
  const requestKeyRef = useRef<string | null>(null);
  const [loadingAssessments, setLoadingAssessments] = useState(false);
  const [creating, setCreating] = useState(false);

  // Portfolio Manager modal state
  const [isPMModalOpen, setIsPMModalOpen] = useState(false);
  const [pmEmail, setPmEmail] = useState("");
  const [pmPassword, setPmPassword] = useState("");
  const [pmDisplayName, setPmDisplayName] = useState("");
  const [creatingPM, setCreatingPM] = useState(false);

  // Fetch portfolio managers
  const fetchPortfolioManagers = async () => {
    if (!subOrgId) return;
    
    const { data, error } = await supabase
      .from("sub_org_memberships")
      .select("user_id, role, created_at")
      .eq("sub_org_id", subOrgId)
      .eq("role", "portfolio_manager");

    if (error) {
      console.error("[SubOrgPortfolios] Error fetching portfolio managers:", error);
      return;
    }

    // Try to get display names from profiles
    if (data && data.length > 0) {
      const userIds = data.map((m) => m.user_id);
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, display_name")
        .in("id", userIds);

      const profileMap = new Map(profiles?.map((p) => [p.id, p.display_name]) || []);
      
      const managersWithNames = data.map((m) => ({
        ...m,
        display_name: profileMap.get(m.user_id) || null,
      }));
      
      setPortfolioManagers(managersWithNames);
    } else {
      setPortfolioManagers([]);
    }
  };

  // Load sub-org and portfolios
  useEffect(() => {
    if (!subOrgId) {
      setError("Sub-organization ID is required.");
      setLoading(false);
      return;
    }

    let active = true;

    (async () => {
      try {
        // 1) Fetch sub-organization details
        const { data: subOrgData, error: subOrgError } = await (supabase as any)
          .from("sub_organizations")
          .select("id, name, client_id")
          .eq("id", subOrgId)
          .single();

        if (subOrgError) throw subOrgError;
        if (!active) return;

        setSubOrg(subOrgData);

        // 1b) Fetch client name for breadcrumb - try RPC first, fallback to direct query
        if (subOrgData?.client_id) {
          const { data: rpcData, error: rpcErr } = await (supabase as any).rpc("admin_get_client_name", { p_client_id: subOrgData.client_id });
          if (!rpcErr && rpcData?.[0]?.name) {
            if (active) {
              setClient({ id: subOrgData.client_id, name: rpcData[0].name });
            }
          } else {
            // Fallback to direct query for non-admin
            const { data: clientData } = await supabase
              .from("clients")
              .select("id, name")
              .eq("id", subOrgData.client_id)
              .single();
            
            if (clientData && active) {
              setClient(clientData);
            }
          }
        }

        // 2) Fetch portfolios for this sub-org
        const { data: portfolioData, error: portfolioError } = await (supabase as any)
          .from("portfolios")
          .select("id, name, category, region, sector, created_at")
          .eq("sub_org_id", subOrgId)
          .eq("archived", false)
          .order("created_at", { ascending: false });

        if (portfolioError) throw portfolioError;
        if (!active) return;

        setPortfolios(portfolioData ?? []);

        // 3) Fetch portfolio managers
        await fetchPortfolioManagers();
      } catch (e: any) {
        if (!active) return;
        console.error("[SubOrgPortfolios] error:", e);
        setError(e?.message || "Failed to load portfolios");
      } finally {
        if (!active) return;
        setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [subOrgId]);

  // Handle category change and fetch assessments
  const handleCategoryChange = async (category: string) => {
    setNewCategory(category);
    setSelectedQuestionnaire("");
    setAvailableAssessments([]);
    setServiceModels([]);
    setLoadingAssessments(true);

    try {
      // Use PM-scoped RPC to avoid raw table access
      const { data: qRows, error } = await (supabase.rpc as any)("pm_list_questionnaires_by_category", {
        p_category: category,
      });

      if (error) {
        console.error("[SubOrgPortfolios] Error fetching questionnaires:", error);
        return;
      }

      if (qRows && qRows.length > 0) {
        // Map RPC results to the shape expected by CategoryQuestionnaire
        const mapped = (qRows as any[]).map((q: any) => ({
          questionnaire_key: q.id,
          is_default: false,
          questionnaires: { id: q.id, meta: q.meta },
        }));
        setAvailableAssessments(mapped as CategoryQuestionnaire[]);

        // Auto-select first option
        if (mapped[0]) {
          setSelectedQuestionnaire(mapped[0].questionnaire_key);
        }
      }
    } catch (e) {
      console.error("[SubOrgPortfolios] Error fetching questionnaires:", e);
    } finally {
      setLoadingAssessments(false);
    }
  };

  const handleCreatePortfolio = async () => {
    if (!newName.trim()) {
      toast({
        title: "Validation error",
        description: "Portfolio name is required.",
        variant: "destructive",
      });
      return;
    }

    if (!newCategory) {
      toast({
        title: "Validation error",
        description: "Category is required.",
        variant: "destructive",
      });
      return;
    }

    if (!selectedQuestionnaire) {
      toast({
        title: "Validation error",
        description: "Assessment is required.",
        variant: "destructive",
      });
      return;
    }

    const usesModels = questionnaireUsesServiceModels(selectedQuestionnaire);
    if (usesModels) {
      const validation = validateServiceModels(serviceModels, {
        required: serviceModelsRequired(selectedQuestionnaire),
        otherDescription: serviceModelOther,
      });
      if (!validation.ok) {
        toast({
          title: "Validation error",
          description: validation.error,
          variant: "destructive",
        });
        return;
      }
    }

    if (!subOrg) {
      toast({
        title: "Error",
        description: "Sub-organization context not available.",
        variant: "destructive",
      });
      return;
    }

    setCreating(true);
    try {
      const questionnaireId = selectedQuestionnaire;

      // Phase 1 (revised) — one transactional RPC creates the portfolio AND its
      // Service Models. Retrying with the same request key returns the existing
      // portfolio instead of creating a duplicate.
      if (!requestKeyRef.current) requestKeyRef.current = crypto.randomUUID();

      const { data: newId, error: rpcError } = await (supabase.rpc as any)(
        "pm_create_portfolio_with_service_models",
        {
          p_sub_org_id: subOrgId,
          p_name: newName.trim(),
          p_category: newCategory,
          p_questionnaire_id: questionnaireId,
          p_models: usesModels ? serviceModels : null,
          p_other_description: serviceModelOther || null,
          p_request_key: requestKeyRef.current,
        },
      );

      if (rpcError) throw rpcError;

      const { data } = await (supabase as any)
        .from("portfolios")
        .select("id, name, category, region, sector, created_at")
        .eq("id", newId)
        .single();

      // Add to local state
      setPortfolios((prev) => [data, ...prev]);
      requestKeyRef.current = null;
      setNewName("");
      setNewCategory("");
      setSelectedQuestionnaire("");
      setAvailableAssessments([]);
      setServiceModels([]);
      setServiceModelOther("");
      setIsModalOpen(false);

      toast({
        title: "Success",
        description: `Portfolio "${data?.name ?? newName.trim()}" created successfully.`,
      });

    } catch (e: any) {
      console.error("[SubOrgPortfolios] create error:", e);
      toast({
        title: "Error",
        description: e?.message || "Failed to create portfolio.",
        variant: "destructive",
      });
    } finally {
      setCreating(false);
    }
  };

  const handleAddPortfolioManager = async () => {
    if (!pmEmail.trim()) {
      toast({
        title: "Validation error",
        description: "Email is required.",
        variant: "destructive",
      });
      return;
    }

    if (!pmPassword.trim()) {
      toast({
        title: "Validation error",
        description: "Password is required.",
        variant: "destructive",
      });
      return;
    }

    if (!subOrgId) {
      toast({
        title: "Error",
        description: "Sub-organization context not available.",
        variant: "destructive",
      });
      return;
    }

    setCreatingPM(true);
    try {
      const payload = {
        sub_org_id: subOrgId,
        email: pmEmail.trim().toLowerCase(),
        password: pmPassword || undefined,
        display_name: pmDisplayName.trim() || null,
        member_role: "portfolio_manager",
      };
      console.log("[create-user payload]", payload);

      const { data, error } = await supabase.functions.invoke("create-user", {
        body: payload,
      });

      if (error) throw new Error(error.message ?? "Edge function error");
      if (data?.error) throw new Error(data.error);

      console.log("[SubOrgPortfolios] Edge function result:", data);

      // Refresh list and close modal
      await fetchPortfolioManagers();
      setPmEmail("");
      setPmPassword("");
      setPmDisplayName("");
      setIsPMModalOpen(false);

      toast({
        title: "Success",
        description: `Portfolio manager "${pmEmail}" added successfully.`,
      });
    } catch (e: any) {
      console.error("[SubOrgPortfolios] add PM error:", e);
      toast({
        title: "Error",
        description: e?.message || "Failed to add portfolio manager.",
        variant: "destructive",
      });
    } finally {
      setCreatingPM(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Loading state
  if (loading) {
    return (
      <AppLayout title="Portfolios — Loading">
        <div className="min-h-screen flex items-center justify-center">
          <div className="space-y-3 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
            <p className="text-sm text-muted-foreground">Loading portfolios…</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  // Error state
  if (error || !subOrg) {
    return (
      <AppLayout title="Portfolios">
        <div className="min-h-screen flex items-center justify-center px-4">
          <Card className="max-w-lg w-full">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-destructive">
                <Building2 className="h-5 w-5" />
                Access Issue
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                {error || "Unable to load portfolios."}
              </p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => navigate("/client/sub-orgs")}
              >
                Back to Sub-Organizations
              </Button>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title={`Portfolios — ${subOrg.name}`}>
      <div className="min-h-screen bg-background px-4 py-6 md:px-8 md:py-8">
        <div className="max-w-5xl mx-auto">
          <ClientAdminBreadcrumb
            clientName={client?.name || "Client"}
            segments={[
              { label: subOrg.name },
            ]}
          />
        </div>

        {/* Header */}
        <header className="max-w-5xl mx-auto mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">
              {subOrg.name}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Manage portfolios under this sub-organization
            </p>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setIsPMModalOpen(true)}>
              <UserPlus className="h-4 w-4 mr-1" />
              Add Portfolio Manager
            </Button>
            <Button size="sm" onClick={() => setIsModalOpen(true)}>
              <Plus className="h-4 w-4 mr-1" />
              Create Portfolio Company
            </Button>
          </div>
        </header>

        {/* Portfolio Managers section */}
        <section className="max-w-5xl mx-auto mb-8">
          <h2 className="text-lg font-medium mb-3">Portfolio Managers</h2>
          {portfolioManagers.length === 0 ? (
            <Card className="p-4">
              <p className="text-sm text-muted-foreground text-center">
                No portfolio managers assigned yet.
              </p>
            </Card>
          ) : (
            <div className="grid gap-2">
              {portfolioManagers.map((pm) => (
                <Card key={pm.user_id} className="p-3">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <Users className="h-4 w-4 text-primary" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-sm">
                        {pm.display_name || "Unnamed User"}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Added {formatDate(pm.created_at)}
                      </p>
                    </div>
                    <span className="text-xs bg-secondary px-2 py-1 rounded">
                      {pm.role}
                    </span>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </section>

        {/* Portfolios list */}
        <main className="max-w-5xl mx-auto">
          <h2 className="text-lg font-medium mb-3">Portfolios</h2>
          {portfolios.length === 0 ? (
            <Card className="p-8 text-center">
              <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="font-medium text-lg mb-2">No portfolios yet</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Create your first portfolio to start managing assessments.
              </p>
              <Button onClick={() => setIsModalOpen(true)}>
                <Plus className="h-4 w-4 mr-1" />
                Create Portfolio Company
              </Button>
            </Card>
          ) : (
            <div className="grid gap-3">
              {portfolios.map((portfolio) => (
                <Card key={portfolio.id} className="hover:border-primary/50 transition-colors">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Building2 className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-medium">{portfolio.name}</h3>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span>{portfolio.category || "Uncategorized"}</span>
                          <span>•</span>
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDate(portfolio.created_at)}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/org-admin/${portfolio.id}/users`)}
                      >
                        <Users className="h-4 w-4 mr-1" />
                        Users
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/org-admin/${portfolio.id}/assessments`)}
                      >
                        <ClipboardList className="h-4 w-4 mr-1" />
                        Assessments
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </main>

        {/* Create Portfolio Company Modal */}
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Portfolio Company</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="portfolio-name">Name *</Label>
                <Input
                  id="portfolio-name"
                  placeholder="Enter portfolio name"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="portfolio-category">Category *</Label>
                <Select value={newCategory} onValueChange={handleCategoryChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORY_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {newCategory && (
                <div className="space-y-2">
                  <Label htmlFor="portfolio-assessment">Assessment *</Label>
                  <Select
                    value={selectedQuestionnaire}
                    onValueChange={(v) => {
                      setSelectedQuestionnaire(v);
                      if (v !== "telecom_v2") setServiceModels([]);
                    }}
                    disabled={loadingAssessments || availableAssessments.length === 0}
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          loadingAssessments
                            ? "Loading assessments..."
                            : availableAssessments.length === 0
                            ? "No assessments available"
                            : "Select assessment"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {availableAssessments.map((q) => (
                        <SelectItem key={q.questionnaire_key} value={q.questionnaire_key}>
                          {getQuestionnaireDisplayTitle(q.questionnaire_key, q.questionnaires?.meta as Record<string, unknown>)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {questionnaireUsesServiceModels(selectedQuestionnaire) && (
                <div className="space-y-2">
                  <Label>Telecom Service Model(s) *</Label>
                  <p className="text-xs text-muted-foreground">
                    Select all that apply. This is stored on the Portfolio Company
                    and inherited by reviewers and respondents.
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {TELECOM_SERVICE_MODEL_OPTIONS.map((m) => {
                      const active = serviceModels.includes(m);
                      return (
                        <button
                          key={m}
                          type="button"
                          onClick={() =>
                            setServiceModels((prev) =>
                              prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m],
                            )
                          }
                          className={`text-xs rounded-full border px-2.5 py-1 transition-colors ${
                            active
                              ? "bg-primary text-primary-foreground border-primary"
                              : "bg-background text-muted-foreground border-border hover:bg-muted"
                          }`}
                        >
                          {m}
                        </button>
                      );
                    })}
                  </div>
                  {serviceModels.includes("Other") && (
                    <Input
                      placeholder="Describe the operating model"
                      value={serviceModelOther}
                      onChange={(e) => setServiceModelOther(e.target.value)}
                    />
                  )}
                  {serviceModels.length === 0 && serviceModelsRequired(selectedQuestionnaire) && (
                    <p className="text-xs text-destructive">Select at least one service model.</p>
                  )}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsModalOpen(false)}
                disabled={creating}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreatePortfolio}
                disabled={creating || !newCategory || !selectedQuestionnaire || (serviceModelsRequired(selectedQuestionnaire) && serviceModels.length === 0)}
              >
                {creating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    Creating…
                  </>
                ) : (
                  "Create Portfolio Company"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Portfolio Manager Modal */}
        <Dialog open={isPMModalOpen} onOpenChange={setIsPMModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Portfolio Manager</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="pm-email">Email *</Label>
                <Input
                  id="pm-email"
                  type="email"
                  placeholder="manager@example.com"
                  value={pmEmail}
                  onChange={(e) => setPmEmail(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pm-password">Password *</Label>
                <Input
                  id="pm-password"
                  type="password"
                  placeholder="Temporary password"
                  value={pmPassword}
                  onChange={(e) => setPmPassword(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pm-display-name">Display Name (optional)</Label>
                <Input
                  id="pm-display-name"
                  placeholder="John Doe"
                  value={pmDisplayName}
                  onChange={(e) => setPmDisplayName(e.target.value)}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsPMModalOpen(false)}
                disabled={creatingPM}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddPortfolioManager}
                disabled={creatingPM || !pmEmail.trim() || !pmPassword.trim()}
              >
                {creatingPM ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    Adding…
                  </>
                ) : (
                  "Add Manager"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}